import { useState } from 'react';
import { Box, Flex } from '@chakra-ui/react';
import moment from 'moment';
import { AnimatePresence, motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

import { ChevronDownIcon, ChevronUpIcon } from 'Asset/icon';
import { Condition, Iteration } from 'Component/others';
import ClockDatetime from 'Component/clock-datetime';
import { TextMedium, TextRegular } from 'UiKit/typography';
import LivestreamDatetime from 'View/host-presence-content/components/livestream-datetime';
import violationCodeToText from 'View/host-presence-content/helpers/violationCodeToText';
import { dropdownAnimation } from 'View/host-presence-content/helpers/dropdownAnimation';
import { hostPresenceOverviewText } from 'Const/host-presence-content/view';

import styles from './styles.module.css';

const ClockInOutSection = ({ hostPresence }) => {
  const { t } = useTranslation('host-presence');

  const clockIn = hostPresence?.clockIn;
  const clockOut = hostPresence?.clockOut;

  const transformedClockInViolations = clockIn?.violations?.map((violation) => violationCodeToText(violation, t));
  const transformedClockOutViolations = clockOut?.violations?.map((violation) => violationCodeToText(violation, t));

  return (
    <Flex flexDir="column" gap="16px">
      <Flex flexDir="column" gap="6px">
        <Flex gap="16px">
          <Box width="45%">
            <ClockDatetime
              label={hostPresenceOverviewText.clockInText}
              time={clockIn?.time ? moment(clockIn.time).format('HH:mm') : '-'}
              date={clockIn?.time ? moment(clockIn.time).format('DD/MM/YY') : '-'}
            />
          </Box>
          <Box width="55%">
            <ClockDatetime
              label={hostPresenceOverviewText.clockOutText}
              time={clockOut?.time ? moment(clockOut.time).format('HH:mm') : '-'}
              date={clockOut?.time ? moment(clockOut.time).format('DD/MM/YY') : '-'}
            />
          </Box>
        </Flex>
        <Flex gap="16px">
          <Flex width="45%" gap="6px" flexWrap="wrap">
            <Iteration
              items={transformedClockInViolations}
              render={(violation) => <Box className={styles['card-report-presence__violation']}>{violation}</Box>}
            />
          </Flex>
          <Flex width="55%" gap="6px" flexWrap="wrap">
            <Iteration
              items={transformedClockOutViolations}
              render={(violation) => <Box className={styles['card-report-presence__violation']}>{violation}</Box>}
            />
          </Flex>
        </Flex>
      </Flex>
      <Box className={styles['dropdown__section']}>
        <Flex gap="16px">
          <Flex width="45%" flexDir="column">
            <TextRegular fontSize="var(--font-caption1)">{t('page.notes')}</TextRegular>
            <TextMedium fontSize="var(--font-body3)">{clockIn?.note || '-'}</TextMedium>
          </Flex>
          <Flex width="55%" flexDir="column">
            <TextRegular fontSize="var(--font-caption1)">{t('page.notes')}</TextRegular>
            <TextMedium fontSize="var(--font-body3)">{clockOut?.note || '-'}</TextMedium>
          </Flex>
        </Flex>
      </Box>
    </Flex>
  );
};

const LivestreamSection = ({ label, livestreamData }) => {
  const { t } = useTranslation('host-presence');

  const transformedLivestreamViolations = livestreamData?.violations?.map((violation) => violationCodeToText(violation, t));
  return (
    <Flex flexDir="column" gap="16px">
      <Flex>
        <LivestreamDatetime
          label={label}
          startDateTime={livestreamData?.clockIn?.time || null}
          endDateTime={livestreamData?.clockOut?.time || null}
          violations={transformedLivestreamViolations}
        />
      </Flex>

      <Box className={styles['dropdown__section']}>
        <Flex gap="16px">
          <Flex width="45%" flexDir="column">
            <TextRegular fontSize="var(--font-caption1)">{t('page.notes')}</TextRegular>
            <TextMedium fontSize="var(--font-body3)">{livestreamData?.clockIn?.note || '-'}</TextMedium>
          </Flex>
          <Flex width="55%" flexDir="column">
            <TextRegular fontSize="var(--font-caption1)">{t('page.notes')}</TextRegular>
            <TextMedium fontSize="var(--font-body3)">{livestreamData?.clockOut?.note || '-'}</TextMedium>
          </Flex>
        </Flex>
      </Box>
    </Flex>
  );
};

const CardReportPresence = ({ data }) => {
  const [isOpen, setIsOpen] = useState(false);

  const totalViolations = data?.hostPresence?.totalViolation;

  return (
    <Box className={styles['card-report-presence__container']}>
      <Box className={styles['card-report-presence__header']} onClick={() => setIsOpen(!isOpen)}>
        <Box className={styles['card-report-presence__header-content']}>
          <TextMedium>{moment(data?.hostPresence?.workDate).format('DD MMMM YYYY')}</TextMedium>
          <Condition statement={totalViolations > 0}>
            <Box className={styles['card-report-presence__violation-count']}>{totalViolations}</Box>
          </Condition>
        </Box>
        <Condition statement={isOpen} fallback={<ChevronDownIcon size={20} />}>
          <ChevronUpIcon size={20} />
        </Condition>
      </Box>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div {...dropdownAnimation}>
            <Box className={styles['card-report-presence__content']}>
              <ClockInOutSection hostPresence={data?.hostPresence} />
              <LivestreamSection label={hostPresenceOverviewText.livestream1Text} livestreamData={data?.streamingSessions?.[0]} />
              <LivestreamSection label={hostPresenceOverviewText.livestream2Text} livestreamData={data?.streamingSessions?.[1]} />
              <LivestreamSection label={hostPresenceOverviewText.livestream3Text} livestreamData={data?.streamingSessions?.[2]} />
            </Box>
          </motion.div>
        )}
      </AnimatePresence>
    </Box>
  );
};

export default CardReportPresence;
